// ModalProvider.tsx
import React, { createContext, useContext, useState } from "react";
import { toaster } from "../components/ui/toaster";

interface StakeModalDetails {
  amount: number;
  isUnstake: boolean;
}

// For the "delete auth" logic
type AuthMethodType = "email" | "wallet" | null;

// ============= Select Network Dialog =============
// Shown when the user has relevant funds (staked balance for unstake, pending
// rewards for claim) on BOTH chains and must pick which network to continue on.
export interface SelectNetworkOption {
  chainId: 8453 | 42161;
  name: string;
  amount: number;
}
export type SelectNetworkPurpose = "unstake" | "claim";

// The shape of our ModalContext
interface ModalContextValue {
  // ============= Stake Modal ============
  isStakeModalVisible: boolean;
  setStakeModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  handleCloseStakeModal: () => void;
  stakeModalDetails: StakeModalDetails | null;
  setStakeModalDetails: React.Dispatch<
    React.SetStateAction<StakeModalDetails | null>
  >;
  showStakeModal: () => void;

  // ============= Delete Modal (API keys) ============
  isDeleteModalVisible: boolean;
  setDeleteModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  showDeleteModal: (apiKey: string, label: string) => void;

  // ============= Edit Modal (API keys) ============
  isEditModalVisible: boolean;
  setEditModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  showEditModal: (apiKey: string, label: string) => void;
  closeEditModal: () => void;

  // ============= "What Is" Modal ============
  isWhatIsModalVisible: boolean;
  setWhatIsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;

  // ============= Common Modal Content (Delete/Edit) ============
  modalContent: { label: string; apiKey: string };
  setModalContent: React.Dispatch<
    React.SetStateAction<{ label: string; apiKey: string }>
  >;

  // ============= Delete Auth Modal ============
  isDeleteAuthModalVisible: boolean;
  deleteAuthType: AuthMethodType;
  deleteAuthIdentifier: string;
  showDeleteAuthModal: (type: "email" | "wallet", identifier: string) => void;
  closeDeleteAuthModal: () => void;

  // ============= Add Email Modal ============
  isAddEmailModalVisible: boolean;
  showAddEmailModal: () => void;
  closeAddEmailModal: () => void;

  // ============= Update Auth Method (email/password) Modal ============
  isUpdateAuthModalVisible: boolean;
  showUpdateAuthModal: () => void;
  closeUpdateAuthModal: () => void;

  // ============= Rename Chat Modal ============
  isRenameChatModalVisible: boolean;
  setIsRenameChatModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  renameChatId: number | null;
  renameChatValue: string;
  showRenameChatModal: (chatId: number, currentName: string) => void;
  closeRenameChatModal: () => void;
  setRenameChatValue: React.Dispatch<React.SetStateAction<string>>;
  renameChat: (chatId: number) => void;

  // ============= Delete Chat Modal ============
  isDeleteChatModalVisible: boolean;
  deleteChatId: number | null;
  deleteChatName: string;
  showDeleteChatModal: (chatId: number, chatName: string) => void;
  closeDeleteChatModal: () => void;

  // ============= Additional Chat States ============
  chatOptionsId: number | null;
  setChatOptionsId: React.Dispatch<React.SetStateAction<number | null>>;
  chatToRenameId: number | null;
  setChatToRenameId: React.Dispatch<React.SetStateAction<number | null>>;
  chatToDeleteId: number | null;
  setChatToDeleteId: React.Dispatch<React.SetStateAction<number | null>>;
  chatToDeleteName: string | null;
  setChatToDeleteName: React.Dispatch<React.SetStateAction<string | null>>;

  isCannotConnectWalletModalVisible: boolean;
  setCannotConnectWalletModalVisible: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  showCannotConnectWalletModal: () => void;
  closeCannotConnectWalletModal: () => void;

  // ============= Error Toast =============
  showErrorMessage: (message: string) => void;

  closeDeleteAccountModal: any
  isDeleteAccountModalVisible: boolean
  showDeleteAccountModal: () => void

  showResetPasswordModal: any
  isResetPasswordModalVisible: any
  closeResetPasswordModal: any

  // ============= Select Network Dialog ============
  isSelectNetworkDialogVisible: boolean;
  selectNetworkOptions: SelectNetworkOption[];
  selectNetworkPurpose: SelectNetworkPurpose;
  showSelectNetworkDialog: (
    options: SelectNetworkOption[],
    purpose: SelectNetworkPurpose,
  ) => void;
  closeSelectNetworkDialog: () => void;

  isClaimBroadcastModalVisible: boolean
  setClaimBroadcastModalVisible: any

  isAprilMorUpgradeModalVisible: boolean
  setAprilMorUpgradeModalVisible: any

  isVerifyEmailModalVisible: boolean
  setVerifyEmailModalVisible: any

  isUpdateAuthPasswordModalVisible: boolean,
  setUpdateAuthPasswordModalVisible: any,
  showUpdateAuthPasswordModal: () => void,
  closeUpdateAuthPasswordModal: () => void,

  closeVerifyEmailModal: () => void,
  showVerifyEmailModal: (email?: string) => void,
  pendingVerifyEmail: string,

  // ============= Announcement Modal ============
  isAnnouncementModalVisible: boolean;
  setAnnouncementModalVisible: React.Dispatch<React.SetStateAction<boolean>>;

  // ============= Create Prepaid Key Modal ============
  isCreatePrepaidModalVisible: boolean;
  setCreatePrepaidModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  showCreatePrepaidModal: () => void;
  closeCreatePrepaidModal: () => void;

  // ============= Prepaid Key Created Modal ============
  isPrepaidCreatedModalVisible: boolean;
  setPrepaidCreatedModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  prepaidCreatedDetails: { apiKey: string; credits: number } | null;
  showPrepaidCreatedModal: (details: { apiKey: string; credits: number }) => void;
  closePrepaidCreatedModal: () => void;

  // ============= Refill Prepaid Key Modal ============
  isRefillPrepaidModalVisible: boolean;
  setRefillPrepaidModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  refillPrepaidKeyId: string | null;
  showRefillPrepaidModal: (keyId?: string) => void;
  closeRefillPrepaidModal: () => void;

  // ============= Prepaid Key Refilled Modal ============
  isPrepaidRefilledModalVisible: boolean;
  setPrepaidRefilledModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  prepaidRefilledDetails: { apiKey: string; credits: number } | null;
  showPrepaidRefilledModal: (details: { apiKey: string; credits: number }) => void;
  closePrepaidRefilledModal: () => void;

}

const ModalContext = createContext<ModalContextValue | null>(null);

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const value = useProvideModalState();
  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
};

export const useModalState = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModalState must be used within a ModalProvider");
  }
  return context;
};

function useProvideModalState(): ModalContextValue {
  // ========== Stake Modal ==========
  const [isStakeModalVisible, setStakeModalVisible] = useState<boolean>(false);
  const [stakeModalDetails, setStakeModalDetails] =
    useState<StakeModalDetails | null>(null);

  const showStakeModal = () => {
    setStakeModalVisible(true);
    setClaimBroadcastModalVisible(false)
  };
  const handleCloseStakeModal = () => setStakeModalVisible(false);

  // ========== Delete Modal (API Keys) ==========
  const [isDeleteModalVisible, setDeleteModalVisible] =
    useState<boolean>(false);
  const showDeleteModal = (apiKey: string, label: string) => {
    setModalContent({ apiKey, label });
    setDeleteModalVisible(true);
  };

  // ========== Edit Modal (API Keys) ==========
  const [isEditModalVisible, setEditModalVisible] = useState<boolean>(false);
  const showEditModal = (apiKey: string, label: string) => {
    setModalContent({ apiKey, label });
    setEditModalVisible(true);
  };
  function closeEditModal() {
    setEditModalVisible(false);
  }
  const [isAprilMorUpgradeModalVisible, setAprilMorUpgradeModalVisible] = useState<boolean>(false);

  // ========== Select Network Dialog ==========
  const [isSelectNetworkDialogVisible, setSelectNetworkDialogVisible] =
    useState<boolean>(false);
  const [selectNetworkOptions, setSelectNetworkOptions] = useState<
    SelectNetworkOption[]
  >([]);
  const [selectNetworkPurpose, setSelectNetworkPurpose] =
    useState<SelectNetworkPurpose>("unstake");

  const showSelectNetworkDialog = (
    options: SelectNetworkOption[],
    purpose: SelectNetworkPurpose,
  ) => {
    setSelectNetworkOptions(options);
    setSelectNetworkPurpose(purpose);
    setSelectNetworkDialogVisible(true);
  };
  const closeSelectNetworkDialog = () => {
    setSelectNetworkDialogVisible(false);
  };

    const [isClaimBroadcastModalVisible, setClaimBroadcastModalVisible] =
    useState<boolean>(false);

    // ========== "What Is" Modal ==========
    const [isWhatIsModalVisible, setWhatIsModalVisible] =
    useState<boolean>(false);


  // ========== Common Modal Content ==========
  const [modalContent, setModalContent] = useState<{
    label: string;
    apiKey: string;
  }>({
    label: "Default Label",
    apiKey: "0x1234567890ABCDEF",
  });

  // ========== Delete Auth Modal (Settings) ==========
  const [isDeleteAuthModalVisible, setDeleteAuthModalVisible] =
    useState<boolean>(false);
  const [deleteAuthType, setDeleteAuthType] = useState<AuthMethodType>(null);
  const [deleteAuthIdentifier, setDeleteAuthIdentifier] = useState<string>("");

  const showDeleteAuthModal = (
    type: "email" | "wallet",
    identifier: string,
  ) => {
    setDeleteAuthType(type);
    setDeleteAuthIdentifier(identifier);
    setDeleteAuthModalVisible(true);
  };
  const closeDeleteAuthModal = () => {
    setDeleteAuthModalVisible(false);
  };

  const [isDeleteAccountModalVisible, setDeleteAccountModalVisible] = useState(false)
  const closeDeleteAccountModal = () => {
    setDeleteAccountModalVisible(false);
  };
  const showDeleteAccountModal = () => {
    setDeleteAccountModalVisible(true);
  };

  const [isResetPasswordModalVisible, setResetPasswordModalVisible] = useState(false)
  const closeResetPasswordModal = () => {
    setResetPasswordModalVisible(false);
  };
  const showResetPasswordModal = () => {
    setResetPasswordModalVisible(true);
  };

  const [isVerifyEmailModalVisible, setVerifyEmailModalVisible] = useState(false)
  // The address the user just requested a change to. VerifyEmailModal needs it
  // to resend the confirmation — it has no input of its own, and previously
  // held a local `email` state that nothing ever set, so its resend button was
  // a permanent no-op.
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState("");
  const closeVerifyEmailModal = () => {
    setVerifyEmailModalVisible(false);
    setPendingVerifyEmail("");
  };
  const showVerifyEmailModal = (email?: string) => {
    if (email !== undefined) setPendingVerifyEmail(email);
    setVerifyEmailModalVisible(true);
  };

  // ========== Add Email Modal (Settings) ==========
  const [isAddEmailModalVisible, setAddEmailModalVisible] = useState(false);
  const showAddEmailModal = () => {
    setAddEmailModalVisible(true);
  };
  const closeAddEmailModal = () => {
    setAddEmailModalVisible(false);
  };

  // ========== Update Auth Method (email/password) Modal (Settings) ==========
  const [isUpdateAuthModalVisible, setUpdateAuthModalVisible] = useState(false);
  const showUpdateAuthModal = () => {
    setUpdateAuthModalVisible(true);
  };
  const closeUpdateAuthModal = () => {
    setUpdateAuthModalVisible(false);
  };

    // ========== Update Auth Method (password) Modal (Settings) ==========
    const [isUpdateAuthPasswordModalVisible, setUpdateAuthPasswordModalVisible] = useState(false);
    const showUpdateAuthPasswordModal = () => {
      setUpdateAuthPasswordModalVisible(true);
    };
    const closeUpdateAuthPasswordModal = () => {
      setUpdateAuthPasswordModalVisible(false);
    };
  

  // ========== Rename Chat Modal ==========
  const [isRenameChatModalVisible, setIsRenameChatModalVisible] =
    useState(false);
  const [renameChatId, setRenameChatId] = useState<number | null>(null);
  const [renameChatValue, setRenameChatValue] = useState("");

  const showRenameChatModal = (chatId: number, currentName: string) => {
    setRenameChatId(chatId);
    setRenameChatValue(currentName);
    setIsRenameChatModalVisible(true);
  };

  const closeRenameChatModal = () => {
    setIsRenameChatModalVisible(false);
    setRenameChatId(null);
    setRenameChatValue("");
  };

  const renameChat = (chatId: number) => {
    if (process.env.NODE_ENV === 'development') console.log(`Renaming chat ${chatId} to new name: ${renameChatValue}`);
    // do your real rename logic (e.g. update global chatHistory or call an API)
    closeRenameChatModal();
  };

  // ========== Delete Chat Modal ==========
  const [isDeleteChatModalVisible, setIsDeleteChatModalVisible] =
    useState(false);
  const [deleteChatId, setDeleteChatId] = useState<number | null>(null);
  const [deleteChatName, setDeleteChatName] = useState<string>("");

  function showDeleteChatModal(chatId: number, chatName: string) {
    setDeleteChatId(chatId);
    setDeleteChatName(chatName);
    setIsDeleteChatModalVisible(true);
  }

  function closeDeleteChatModal() {
    setIsDeleteChatModalVisible(false);
    setDeleteChatId(null);
    setDeleteChatName("");
  }

  // ========== Additional Chat State from ChatContainer ==========
  const [chatOptionsId, setChatOptionsId] = useState<number | null>(null);
  const [chatToRenameId, setChatToRenameId] = useState<number | null>(null);
  const [chatToDeleteId, setChatToDeleteId] = useState<number | null>(null);
  const [chatToDeleteName, setChatToDeleteName] = useState<string | null>(null);

  // ========== "Cannot Connect Wallet" Modal ==========
  const [
    isCannotConnectWalletModalVisible,
    setCannotConnectWalletModalVisible,
  ] = useState<boolean>(false);

  const showCannotConnectWalletModal = () => {
    setCannotConnectWalletModalVisible(true);
  };

  const closeCannotConnectWalletModal = () => {
    setCannotConnectWalletModalVisible(false);
  };

  // Errors surface through the global Chakra Toaster (top-right, auto-dismiss,
  // stacks, closable). showErrorMessage keeps its signature so all ~19 callers
  // are unchanged.
  const showErrorMessage = (message: string) => {
    toaster.create({
      type: "error",
      description: message,
      duration: 5000,
    });
  };

  // ========== Announcement Modal ==========
  const [isAnnouncementModalVisible, setAnnouncementModalVisible] = useState<boolean>(false);

  // ========== Create Prepaid Key Modal ==========
  const [isCreatePrepaidModalVisible, setCreatePrepaidModalVisible] = useState<boolean>(false);
  const showCreatePrepaidModal = () => setCreatePrepaidModalVisible(true);
  const closeCreatePrepaidModal = () => setCreatePrepaidModalVisible(false);

  // ========== Prepaid Key Created Modal ==========
  const [isPrepaidCreatedModalVisible, setPrepaidCreatedModalVisible] = useState<boolean>(false);
  const [prepaidCreatedDetails, setPrepaidCreatedDetails] = useState<{ apiKey: string; credits: number } | null>(null);
  const showPrepaidCreatedModal = (details: { apiKey: string; credits: number }) => {
    setPrepaidCreatedDetails(details);
    setPrepaidCreatedModalVisible(true);
  };
  const closePrepaidCreatedModal = () => {
    setPrepaidCreatedModalVisible(false);
    setPrepaidCreatedDetails(null);
  };

  // ========== Refill Prepaid Key Modal ==========
  const [isRefillPrepaidModalVisible, setRefillPrepaidModalVisible] = useState<boolean>(false);
  const [refillPrepaidKeyId, setRefillPrepaidKeyId] = useState<string | null>(null);
  const showRefillPrepaidModal = (keyId?: string) => {
    setRefillPrepaidKeyId(keyId || null);
    setRefillPrepaidModalVisible(true);
  };
  const closeRefillPrepaidModal = () => {
    setRefillPrepaidModalVisible(false);
    setRefillPrepaidKeyId(null);
  };

  // ========== Prepaid Key Refilled Modal ==========
  const [isPrepaidRefilledModalVisible, setPrepaidRefilledModalVisible] = useState<boolean>(false);
  const [prepaidRefilledDetails, setPrepaidRefilledDetails] = useState<{ apiKey: string; credits: number } | null>(null);
  const showPrepaidRefilledModal = (details: { apiKey: string; credits: number }) => {
    setPrepaidRefilledDetails(details);
    setPrepaidRefilledModalVisible(true);
  };
  const closePrepaidRefilledModal = () => {
    setPrepaidRefilledModalVisible(false);
    setPrepaidRefilledDetails(null);
  };

  return {
    isUpdateAuthPasswordModalVisible,
    setUpdateAuthPasswordModalVisible,
    showUpdateAuthPasswordModal,
    closeUpdateAuthPasswordModal,
    // ===== Stake Modal =====
    isStakeModalVisible,
    setStakeModalVisible,
    handleCloseStakeModal,
    stakeModalDetails,
    setStakeModalDetails,
    showStakeModal,

    // ===== Delete Modal (API Keys) =====
    isDeleteModalVisible,
    setDeleteModalVisible,
    showDeleteModal,

    // ===== Edit Modal (API Keys) =====
    isEditModalVisible,
    setEditModalVisible,
    showEditModal,
    closeEditModal,

    // ===== "What Is" Modal =====
    isWhatIsModalVisible,
    setWhatIsModalVisible,

    // ===== Common Content (API keys) =====
    modalContent,
    setModalContent,

    // ===== Delete Auth Modal (Settings) =====
    isDeleteAuthModalVisible,
    deleteAuthType,
    deleteAuthIdentifier,
    showDeleteAuthModal,
    closeDeleteAuthModal,

    // ===== Add Email Modal (Settings) =====
    isAddEmailModalVisible,
    showAddEmailModal,
    closeAddEmailModal,

    // ===== Update Auth Method (Settings) =====
    isUpdateAuthModalVisible,
    showUpdateAuthModal,
    closeUpdateAuthModal,

    // ===== Rename Chat Modal =====
    isRenameChatModalVisible,
    setIsRenameChatModalVisible,
    renameChatId,
    renameChatValue,
    showRenameChatModal,
    closeRenameChatModal,
    setRenameChatValue,
    renameChat,

    // ===== Delete Chat Modal =====
    isDeleteChatModalVisible,
    deleteChatId,
    deleteChatName,
    showDeleteChatModal,
    closeDeleteChatModal,

    // ===== Additional Chat States =====
    chatOptionsId,
    setChatOptionsId,
    chatToRenameId,
    setChatToRenameId,
    chatToDeleteId,
    setChatToDeleteId,
    chatToDeleteName,
    setChatToDeleteName,

    isCannotConnectWalletModalVisible,
    setCannotConnectWalletModalVisible,
    showCannotConnectWalletModal,
    closeCannotConnectWalletModal,

    showErrorMessage,

    closeDeleteAccountModal,
    isDeleteAccountModalVisible,
    showDeleteAccountModal,

    closeResetPasswordModal,
    isResetPasswordModalVisible,
    showResetPasswordModal,

    isSelectNetworkDialogVisible,
    selectNetworkOptions,
    selectNetworkPurpose,
    showSelectNetworkDialog,
    closeSelectNetworkDialog,
    isClaimBroadcastModalVisible,
    setClaimBroadcastModalVisible,

    isAprilMorUpgradeModalVisible,
    setAprilMorUpgradeModalVisible,

    isVerifyEmailModalVisible,
    setVerifyEmailModalVisible,
    closeVerifyEmailModal,
    showVerifyEmailModal,
    pendingVerifyEmail,

    // ===== Announcement Modal =====
    isAnnouncementModalVisible,
    setAnnouncementModalVisible,

    // ===== Create Prepaid Key Modal =====
    isCreatePrepaidModalVisible,
    setCreatePrepaidModalVisible,
    showCreatePrepaidModal,
    closeCreatePrepaidModal,

    // ===== Prepaid Key Created Modal =====
    isPrepaidCreatedModalVisible,
    setPrepaidCreatedModalVisible,
    prepaidCreatedDetails,
    showPrepaidCreatedModal,
    closePrepaidCreatedModal,

    // ===== Refill Prepaid Key Modal =====
    isRefillPrepaidModalVisible,
    setRefillPrepaidModalVisible,
    refillPrepaidKeyId,
    showRefillPrepaidModal,
    closeRefillPrepaidModal,

    // ===== Prepaid Key Refilled Modal =====
    isPrepaidRefilledModalVisible,
    setPrepaidRefilledModalVisible,
    prepaidRefilledDetails,
    showPrepaidRefilledModal,
    closePrepaidRefilledModal,
  };
}


export default useProvideModalState;
